import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Button, Result, Spin, Alert, Modal } from 'antd';
import { CheckCircleOutlined, ClockCircleOutlined, ExclamationCircleOutlined, CloseOutlined } from '@ant-design/icons';
import { orderAPI } from '../services/api';
import { OrderDetail, PaymentMethod } from '../types/order';
import { showErrorToast, showToast } from '../utils/toast';
import { useTelegram } from '../contexts/TelegramContext';

const OrderStatus: React.FC = () => {
  const { tradeNo } = useParams<{ tradeNo: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [orderDetail, setOrderDetail] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedPayment, setSelectedPayment] = useState<number | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  // 二维码支付（桌面端展示，移动端跳转）
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const { openLink, isInTelegram } = useTelegram();

  useEffect(() => {
    if (tradeNo) {
      fetchOrderDetail();
      fetchPaymentMethods();
      // 如果是从支付页面返回，自动检查订单状态
      const fromPayment = searchParams.get('from') === 'payment';
      if (fromPayment) {
        const timer = setTimeout(() => {
          checkOrderStatus();
        }, 2000);
        return () => clearTimeout(timer);
      }
    }
  }, [tradeNo]);

  // 自动轮询检查订单状态
  useEffect(() => {
    if (tradeNo && orderDetail && (orderDetail.status === 0 || orderDetail.status === 1)) {
      intervalRef.current = setInterval(async () => {
        try {
          const response = await orderAPI.checkOrder(tradeNo);
          // console.log(response.data.data);
          if (response.data.data !== orderDetail.status) {
            await fetchOrderDetail();
          }
        } catch (error) {
          console.error('自动检查订单状态失败:', error);
        }
      }, 3000); // 每3秒检查一次
    } else {
      // 当订单状态不是等待支付或开通中时，清除定时器
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [tradeNo, orderDetail]);

  const fetchOrderDetail = async () => {
    if (!tradeNo) return;

    try {
      setLoading(true);
      const response = await orderAPI.getOrderDetail(tradeNo);
      setOrderDetail(response.data.data);
    } catch (error: any) {
      showErrorToast(error.response?.data?.message || '获取订单详情失败');
    } finally {
      setLoading(false);
    }
  };

  const checkOrderStatus = async () => {
    if (!tradeNo) return;

    try {
      setChecking(true);
      await fetchOrderDetail();
    } catch (error: any) {
      showErrorToast(error.response?.data?.message || '检查订单状态失败');
    } finally {
      setChecking(false);
    }
  };



  const fetchPaymentMethods = async () => {
    try {
      const response = await orderAPI.getPaymentMethods();
      const methods = response.data.data || [];
      setPaymentMethods(methods);
      if (methods.length > 0) {
        setSelectedPayment(methods[0].id);
      }
    } catch (error: any) {
      console.error('获取支付方式失败:', error);
    }
  };

  const handleRepayment = () => {
    if (!orderDetail) return;
    setShowPaymentModal(true);
  };

  const handlePayment = async () => {
    if (!orderDetail || !selectedPayment) {
      showErrorToast('请选择支付方式');
      return;
    }

    try {
      setPaymentProcessing(true);
      const response = await orderAPI.checkOut(orderDetail.trade_no, selectedPayment);
      const paymentData = response.data;

      if (paymentData.type === -1) {
        showToast('支付成功！订单已完成，正在刷新...');
        setShowPaymentModal(false);
        setTimeout(() => {
          checkOrderStatus();
        }, 1500);
      } else if (paymentData.type === 0) {
        // 二维码支付：桌面端展示二维码，移动端直接跳转
        const payLink: string = paymentData.data;
        const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

        if (isMobileDevice) {
          if (isInTelegram) {
            openLink(payLink);
          } else {
            window.location.href = payLink;
          }
          showToast('已跳转到支付页面，请完成支付后返回刷新状态');
          setShowPaymentModal(false);
        } else {
          // 桌面端：生成二维码并展示模态框
          const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(payLink)}`;
          setQrCodeUrl(qrUrl);
          setShowQrModal(true);
          setShowPaymentModal(false);
          showToast('请使用手机扫码完成支付');
        }
      } else if (paymentData.type === 1) {
        // 跳转到支付页面
        if (isInTelegram) {
          openLink(paymentData.data);
        } else {
          window.location.href = paymentData.data;
        }
        showToast('支付页面已在新窗口打开，请完成支付后点击刷新状态');
        setShowPaymentModal(false);
      }
    } catch (error: any) {
      showErrorToast(error.response?.data?.message || '支付失败');
    } finally {
      setPaymentProcessing(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!orderDetail) return;

    try {
      await orderAPI.cancelOrder(orderDetail.trade_no);
      showToast('订单已取消');
      await fetchOrderDetail(); // 刷新订单详情
    } catch (error: any) {
      showErrorToast(error.response?.data?.message || '取消订单失败');
    }
  };

  const getStatusInfo = (status: number) => {
    switch (status) {
      case 0:
        return {
          status: 'warning',
          title: '等待支付',
          subTitle: '订单已创建，请完成支付',
          icon: <ClockCircleOutlined className="text-yellow-500" />
        };
      case 1:
        return {
          status: 'info',
          title: '开通中',
          subTitle: '支付成功，正在为您开通服务',
          icon: <ClockCircleOutlined className="text-blue-500" />
        };
      case 2:
        return {
          status: 'error',
          title: '订单已取消',
          subTitle: '订单已被取消',
          icon: <ExclamationCircleOutlined className="text-red-500" />
        };
      case 3:
        return {
          status: 'success',
          title: '订单完成',
          subTitle: '服务已成功开通，感谢您的购买！',
          icon: <CheckCircleOutlined className="text-green-500" />
        };
      default:
        return {
          status: 'info',
          title: '未知状态',
          subTitle: '订单状态未知',
          icon: <ExclamationCircleOutlined className="text-gray-500" />
        };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  if (!orderDetail) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Result
          status="404"
          title="订单不存在"
          subTitle="未找到指定的订单信息"
          extra={
            <Button type="primary" onClick={() => navigate('/plans')}>
              返回计划列表
            </Button>
          }
        />
      </div>
    );
  }

  const statusInfo = getStatusInfo(orderDetail.status);

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
      <Card className="shadow-sm">
        <Result
          status={statusInfo.status as any}
          title={<span className="text-lg sm:text-xl">{statusInfo.title}</span>}
          subTitle={<span className="text-sm sm:text-base">{statusInfo.subTitle}</span>}
          icon={statusInfo.icon}
          extra={[
            <div key="order-info" className="mb-6">
              <div className="bg-gray-50 p-3 sm:p-4 rounded-lg space-y-3">
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                  <span className="text-gray-600 text-sm">订单号:</span>
                  <span className="font-mono text-sm break-all">{orderDetail.trade_no}</span>
                </div>
                {/* 详细费用构成 */}
                <div className="border-t border-gray-200 pt-3 mt-3">
                  <div className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                    费用明细
                  </div>
                  <div className="space-y-2">
                    {/* 套餐原价 - 计算方式：总金额 + 所有抵扣金额 */}
                    <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                      <span className="text-gray-600 text-sm">套餐原价:</span>
                      <span className="text-sm">¥{(
                        (orderDetail.total_amount + 
                         (orderDetail.discount_amount || 0) + 
                         (orderDetail.balance_amount || 0) + 
                         (orderDetail.surplus_amount || 0)) / 100
                      ).toFixed(2)}</span>
                    </div>

                    {/* 优惠券抵扣 */}
                    {orderDetail.discount_amount && orderDetail.discount_amount > 0 && (
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                        <span className="text-green-600 text-sm flex items-center">
                          优惠券抵扣
                          <span className="ml-1 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                            {orderDetail.coupon?.type === 1 ? '金额券' : '折扣券'}
                          </span>
                        </span>
                        <span className="text-green-600 text-sm font-medium">-¥{(orderDetail.discount_amount / 100).toFixed(2)}</span>
                      </div>
                    )}

                    {/* 余额抵扣 */}
                    {orderDetail.balance_amount && orderDetail.balance_amount > 0 && (
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                        <span className="text-orange-600 text-sm flex items-center">
                          余额抵扣
                          <span className="ml-1 text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">
                            已抵扣
                          </span>
                        </span>
                        <span className="text-orange-600 text-sm font-medium">-¥{(orderDetail.balance_amount / 100).toFixed(2)}</span>
                      </div>
                    )}

                    {/* 剩余价值抵扣 */}
                    {orderDetail.surplus_amount && orderDetail.surplus_amount > 0 && (
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                        <span className="text-blue-600 text-sm flex items-center">
                          剩余价值抵扣
                          <span className="ml-1 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                            已抵扣
                          </span>
                        </span>
                        <span className="text-blue-600 text-sm font-medium">-¥{(orderDetail.surplus_amount / 100).toFixed(2)}</span>
                      </div>
                    )}

                    {/* 实际支付金额 */}
                    <div className="border-t border-gray-200 pt-2 mt-3">
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                        <span className="text-gray-900 text-sm font-semibold">实际支付:</span>
                        <span className="font-semibold text-lg text-primary-600">¥{(orderDetail.total_amount / 100).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 优惠提示 */}
                {((orderDetail.balance_amount && orderDetail.balance_amount > 0) || 
                  (orderDetail.surplus_amount && orderDetail.surplus_amount > 0) || 
                  (orderDetail.discount_amount && orderDetail.discount_amount > 0)) && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-3">
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                      <span className="text-sm font-medium text-green-800">恭喜您享受了优惠！</span>
                    </div>
                    <div className="mt-1 text-xs text-green-600">
                      {orderDetail.discount_amount && orderDetail.discount_amount > 0 && `优惠券抵扣 ¥${(orderDetail.discount_amount / 100).toFixed(2)}`}
                      {orderDetail.discount_amount && orderDetail.discount_amount > 0 && (orderDetail.balance_amount || orderDetail.surplus_amount) && ' + '}
                      {orderDetail.balance_amount && orderDetail.balance_amount > 0 && `余额抵扣 ¥${(orderDetail.balance_amount / 100).toFixed(2)}`}
                      {orderDetail.balance_amount && orderDetail.balance_amount > 0 && orderDetail.surplus_amount && orderDetail.surplus_amount > 0 && ' + '}
                      {orderDetail.surplus_amount && orderDetail.surplus_amount > 0 && `剩余价值抵扣 ¥${(orderDetail.surplus_amount / 100).toFixed(2)}`}
                      ，为您节省了不少费用！
                    </div>
                  </div>
                )}
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                  <span className="text-gray-600 text-sm">创建时间:</span>
                  <span className="text-sm">{new Date(orderDetail.created_at * 1000).toLocaleString()}</span>
                </div>
                {orderDetail.plan && (
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                    <span className="text-gray-600 text-sm">订阅计划:</span>
                    <span className="text-sm font-medium">{orderDetail.plan.name}</span>
                  </div>
                )}
                {orderDetail.coupon && (
                  <>
                    <div className="border-t border-gray-200 pt-3 mt-3">
                      <div className="text-sm font-medium text-gray-700 mb-2">优惠信息</div>
                      <div className="space-y-2">
                        <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                          <span className="text-gray-600 text-sm">优惠券:</span>
                          <span className="text-sm">{orderDetail.coupon.name}</span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                          <span className="text-gray-600 text-sm">优惠类型:</span>
                          <span className="text-sm">{orderDetail.coupon.type === 1 ? '金额券' : orderDetail.coupon.type === 2 ? '比例券' : '未知类型'}</span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                          <span className="text-gray-600 text-sm">优惠值:</span>
                          <span className="text-sm">
                            {orderDetail.coupon.type === 1
                              ? `¥${(orderDetail.coupon.value / 100).toFixed(2)}`
                              : orderDetail.coupon.type === 2
                                ? `${orderDetail.coupon.value}%`
                                : orderDetail.coupon.value
                            }
                          </span>
                        </div>
                        {orderDetail.discount_amount && orderDetail.discount_amount > 0 && (
                          <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                            <span className="text-gray-600 text-sm">实际优惠:</span>
                            <span className="text-green-600 font-medium text-sm">-¥{(orderDetail.discount_amount / 100).toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>,
            <div key="actions" className="flex flex-col sm:flex-row gap-3 sm:justify-center">
              {orderDetail.status === 0 && (
                <Button
                  type="primary"
                  onClick={handleRepayment}
                  className="w-full sm:w-auto"
                  size="large"
                >
                  重新支付
                </Button>
              )}
              {orderDetail.status === 0 && (
                <Button
                  icon={<CloseOutlined />}
                  onClick={handleCancelOrder}
                  className="w-full sm:w-auto"
                  size="large"
                  danger
                >
                  取消订单
                </Button>
              )}
              {(orderDetail.status === 0 || orderDetail.status === 1) && (
                <Button
                  onClick={checkOrderStatus}
                  loading={checking}
                  className="w-full sm:w-auto"
                  size="large"
                >
                  刷新状态
                </Button>
              )}

            </div>
          ]}
        />

        {orderDetail.status === 1 && (
          <Alert
            message="处理中"
            description="您的订单正在处理中，通常在几分钟内完成。如果长时间未完成，请联系客服。"
            type="info"
            showIcon
            className="mt-4"
          />
        )}

        {(orderDetail.status === 0 || orderDetail.status === 1) && (
          <Alert
            message="请尽快完成支付"
            description="支付完成后，订单状态会自动更新。"
            type="info"
            showIcon
            className="mt-4"
          />
        )}

        {orderDetail.status === 3 && (
          <Alert
            message="开通成功"
            description="您的服务已成功开通，现在可以开始使用了。请前往节点页面获取连接信息。"
            type="success"
            showIcon
            className="mt-4"
            action={
              <Button size="small" onClick={() => navigate('/nodes')}>
                查看节点
              </Button>
            }
          />
        )}
      </Card>

      {/* 支付模态框 */}
      <Modal
        title="选择支付方式"
        open={showPaymentModal}
        onCancel={() => setShowPaymentModal(false)}
        footer={null}
        width={600}
        className="mobile-modal"
        centered
      >
        {orderDetail && (
          <div className="space-y-4">
            {/* 订单基本信息 */}
            <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-3">
                <span className="text-sm text-gray-600">订单号:</span>
                <span className="font-mono text-sm break-all">{orderDetail.trade_no}</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                <span className="text-sm text-gray-600">套餐名称:</span>
                <span className="text-sm font-medium">{orderDetail.plan?.name}</span>
              </div>
            </div>

            {/* 详细费用明细 */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
              <h4 className="text-sm font-semibold text-blue-900 mb-3 flex items-center">
                <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                费用明细
              </h4>
              <div className="space-y-2">
                {/* 套餐原价 */}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">套餐原价:</span>
                  <span>¥{(
                    (orderDetail.total_amount + 
                     (orderDetail.discount_amount || 0) + 
                     (orderDetail.balance_amount || 0) + 
                     (orderDetail.surplus_amount || 0)) / 100
                  ).toFixed(2)}</span>
                </div>

                {/* 优惠券抵扣 */}
                {orderDetail.discount_amount && orderDetail.discount_amount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span className="flex items-center">
                      优惠券抵扣
                      <span className="ml-1 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                        {orderDetail.coupon?.type === 1 ? '金额券' : '折扣券'}
                      </span>
                    </span>
                    <span>-¥{(orderDetail.discount_amount / 100).toFixed(2)}</span>
                  </div>
                )}

                {/* 余额抵扣 */}
                {orderDetail.balance_amount && orderDetail.balance_amount > 0 && (
                  <div className="flex justify-between text-sm text-orange-600">
                    <span className="flex items-center">
                      余额抵扣
                      <span className="ml-1 text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">
                        已抵扣
                      </span>
                    </span>
                    <span>-¥{(orderDetail.balance_amount / 100).toFixed(2)}</span>
                  </div>
                )}

                {/* 剩余价值抵扣 */}
                {orderDetail.surplus_amount && orderDetail.surplus_amount > 0 && (
                  <div className="flex justify-between text-sm text-blue-600">
                    <span className="flex items-center">
                      剩余价值抵扣
                      <span className="ml-1 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                        已抵扣
                      </span>
                    </span>
                    <span>-¥{(orderDetail.surplus_amount / 100).toFixed(2)}</span>
                  </div>
                )}

                {/* 实际支付金额 */}
                <div className="border-t border-blue-200 pt-2 mt-3">
                  <div className="flex justify-between text-base font-semibold">
                    <span className="text-gray-900">实际支付:</span>
                    <span className="text-primary-600">¥{(orderDetail.total_amount / 100).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 优惠提示 */}
            {((orderDetail.balance_amount && orderDetail.balance_amount > 0) || 
              (orderDetail.surplus_amount && orderDetail.surplus_amount > 0) || 
              (orderDetail.discount_amount && orderDetail.discount_amount > 0)) ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                  <span className="text-sm font-medium text-green-800">恭喜您享受了优惠！</span>
                </div>
                <div className="mt-1 text-xs text-green-600">
                  {orderDetail.discount_amount && orderDetail.discount_amount > 0 && `优惠券抵扣 ¥${(orderDetail.discount_amount / 100).toFixed(2)}`}
                  {orderDetail.discount_amount && orderDetail.discount_amount > 0 && (orderDetail.balance_amount || orderDetail.surplus_amount) && ' + '}
                  {orderDetail.balance_amount && orderDetail.balance_amount > 0 && `余额抵扣 ¥${(orderDetail.balance_amount / 100).toFixed(2)}`}
                  {orderDetail.balance_amount && orderDetail.balance_amount > 0 && orderDetail.surplus_amount && orderDetail.surplus_amount > 0 && ' + '}
                  {orderDetail.surplus_amount && orderDetail.surplus_amount > 0 && `剩余价值抵扣 ¥${(orderDetail.surplus_amount / 100).toFixed(2)}`}
                  ，为您节省了不少费用！
                </div>
              </div>
            ) : (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-orange-500 rounded-full mr-2"></div>
                  <span className="text-sm font-medium text-orange-800">支付提示</span>
                </div>
                <div className="mt-1 text-xs text-orange-600">
                  支付时将优先使用您的账户余额，让每一分钱都更值钱！
                </div>
              </div>
            )}

            <div>
              <h4 className="text-base font-medium text-gray-900 mb-4">选择支付方式:</h4>
              <div className="space-y-3">
                {paymentMethods.map((method) => (
                  <div
                    key={method.id}
                    className={`border rounded-lg p-4 cursor-pointer transition-colors ${selectedPayment === method.id
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                      }`}
                    onClick={() => setSelectedPayment(method.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center flex-1">
                        <div className={`w-5 h-5 rounded-full border-2 mr-3 flex-shrink-0 ${selectedPayment === method.id
                            ? 'border-primary-500 bg-primary-500'
                            : 'border-gray-300'
                          }`}>
                          {selectedPayment === method.id && (
                            <div className="w-3 h-3 bg-white rounded-full mx-auto mt-0.5"></div>
                          )}
                        </div>
                        <span className="font-medium text-base">{method.name}</span>
                      </div>
                      {(method.handling_fee_fixed || method.handling_fee_percent) && (
                        <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                          手续费: {method.handling_fee_fixed ? `¥${(method.handling_fee_fixed / 100).toFixed(2)}` : ''}
                          {method.handling_fee_percent ? `${method.handling_fee_percent}%` : ''}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button
                onClick={() => setShowPaymentModal(false)}
                className="w-full sm:flex-1 order-2 sm:order-1"
                size="large"
              >
                取消
              </Button>
              <Button
                type="primary"
                onClick={handlePayment}
                loading={paymentProcessing}
                disabled={!selectedPayment}
                className="w-full sm:flex-1 order-1 sm:order-2"
                size="large"
              >
                确认支付
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* 扫码支付模态框（桌面端） */}
      <Modal
        title="扫码支付"
        open={showQrModal}
        onCancel={() => setShowQrModal(false)}
        footer={null}
        width={420}
        className="mobile-modal"
        centered
      >
        <div className="flex flex-col items-center space-y-4">
          {qrCodeUrl && (
            <img
              src={qrCodeUrl}
              alt="支付二维码"
              className="w-60 h-60 rounded-md border"
            />
          )}
          <Alert
            type="info"
            message="请使用手机扫码完成支付"
            description="支付完成后，点击下方按钮刷新订单状态。"
            showIcon
          />
          <div className="flex gap-3 w-full">
            <Button
              type="primary"
              block
              onClick={() => {
                setShowQrModal(false);
                checkOrderStatus();
              }}
            >
              已完成支付，刷新状态
            </Button>
            <Button
              block
              onClick={() => setShowQrModal(false)}
            >
              稍后再说
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default OrderStatus;